import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { type PimVariantAttributeV1 } from '@redteclab/api/clients/bully'

import { Divider } from 'base-ui'

import { priceGetFormattedPrice } from '../../../../../price'
import { ProductPicture } from '../../../../../product'
import { productAccessorGetBestOffer } from '../../../../model/productAccessor/productAccessorGetBestOffer'
import { variantAttributeAccessorGetLabelText } from '../../../../model/variantAttributeAccessor/variantAttributeAccessorGetLabelText'
import { useProductPageContext } from '../../../productPageContext'

import { ProductOfferCard } from './ProductOfferCard'

export const ProductOfferList: FC = () => {
  const { currentPrescriptionType, currentVariant } = useProductPageContext()
  const intl = useIntl()

  const bestOffer = currentVariant.offers
    ? productAccessorGetBestOffer({ offers: currentVariant.offers })
    : undefined

  const otherOffers = (currentVariant.offers ?? []).filter(
    (offer) =>
      offer.offerId !== bestOffer?.offerId &&
      typeof offer.offerPrice?.price === 'number',
  )

  const productAttributesLabelText = [
    ...(currentVariant.variant_attributes ?? []).map(
      (attribute: PimVariantAttributeV1) => {
        const variantLabel = variantAttributeAccessorGetLabelText(
          attribute,
          intl,
        )

        return variantLabel ? `${variantLabel}: ${attribute.value}` : null
      },
    ),
    currentVariant.pharma_form?.label ?? null,
  ]
    .filter(Boolean)
    .join(' | ')

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-6">
        <ProductPicture
          className="flex shrink-0 rounded-2xl bg-light-primary-low p-2"
          imageProps={{
            alt: currentVariant.name,
            height: 64,
            src: currentVariant.images?.at(0),
            width: 64,
          }}
        />
        <div className="flex flex-col">
          <span className="font-medium">{currentVariant.name}</span>
          <span className="text-dark-primary-strong">
            {productAttributesLabelText}
          </span>
        </div>
      </div>

      <ProductOfferCard
        offer={bestOffer}
        prescriptionType={currentPrescriptionType}
      />
      <Divider />
      {otherOffers.length > 0 ? (
        <>
          <h2 className="text-m font-medium">
            <FormattedMessage
              id="productOffer.otherOffers"
              values={{
                offersCount: otherOffers.length,
                price:
                  // the offers without price are already filtered out
                  typeof otherOffers[0].offerPrice?.price === 'number'
                    ? priceGetFormattedPrice(
                        otherOffers[0].offerPrice.price,
                        intl,
                      )
                    : '',
              }}
            />
          </h2>
          <ul className="flex flex-col gap-6">
            {otherOffers.map((offer) => (
              <li key={offer.offerId}>
                <ProductOfferCard
                  offer={offer}
                  prescriptionType={currentPrescriptionType}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}
